import React, { useState } from "react";
import { IconAlertCircle, IconRefresh } from "@tabler/icons-react";

interface RequestErrorStateProps {
  errorMessage?: string;
  onRetry: () => Promise<void> | void;
  title?: string;
}

export const RequestErrorState: React.FC<RequestErrorStateProps> = ({
  errorMessage = "An unexpected error occurred while fetching your data records. Please try again.",
  onRetry,
  title = "Failed to load details",
}) => {
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetryClick = async () => {
    setIsRetrying(true);
    try {
      await onRetry();
    } catch (err) {
      // Keep state clear if parent handle fails
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="w-full mx-auto bg-white border border-gray-100 rounded-2xl p-6 shadow-sm flex flex-col items-center text-center transition-all duration-200">
      {/* Muted Neutral-Gray Icon Medallion Badge */}
      <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 mb-4 border border-gray-100/50">
        <IconAlertCircle size={22} stroke={2} />
      </div>

      {/* Typography Elements Hierarchy */}
      <h3 className="text-base font-bold text-[#0D1B2E] tracking-tight mb-1.5">
        {title}
      </h3>

      <p className="text-xs sm:text-sm text-gray-400 font-medium leading-relaxed max-w-xs mb-6">
        {errorMessage}
      </p>

      {/* Compact Interactive Muted Action Control Button */}
      <button
        onClick={handleRetryClick}
        disabled={isRetrying}
        type="button"
        className="min-h-10 px-4 py-2 border border-gray-200 text-gray-600 hover:text-[#0D1B2E] hover:bg-gray-50 disabled:bg-gray-50/50 rounded-xl text-xs font-bold transition-all duration-200 hover:scale-[1.015] active:scale-[0.985] flex items-center justify-center gap-2 focus:outline-none disabled:opacity-50 disabled:scale-100 shrink-0"
      >
        <IconRefresh
          size={14}
          stroke={2.5}
          className={`text-gray-400 group-hover:text-gray-600 transition-transform ${isRetrying ? "animate-spin text-gray-500" : ""}`}
        />
        {isRetrying ? "Retrying connection..." : "Try again"}
      </button>
    </div>
  );
};

export default RequestErrorState;
